import {
  faTrash,
  faMapMarkerAlt,
  faEye,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { Attribute } from "@/components/form-property/property-types";
import { generatePropertyId } from "@/utils/propertyId";

type PropertyCardProps = {
  id: number;
  codigo?: number; // Código auto-incrementado
  title: string;
  description: string;
  images: string[];
  price?: string;
  type?: string; // Adicionado para gerar o ID
  tagKanlo?: string; // Adicionado de volta o tagKanlo
  status: string;
  condominiumPrice?: string;
  iptuPrice?: string;
  attributes: Attribute[];
  additionalInfo: string;
  updatedAt?: string;
  captador?: number[] | string;
};

const getDaysAgo = (dateString: string): string => {
  const updatedDate = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - updatedDate.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "hoje";
  } else if (diffDays === 1) {
    return "há 1 dia";
  } else {
    return `há ${diffDays} dias`;
  }
};

export const PropertyCard = ({
  id,
  title,
  description,
  images,
  price = "R$ 0,00",
  type,
  codigo,
  tagKanlo = "N/A",
  condominiumPrice = "N/A",
  iptuPrice = "N/A",
  attributes = [],
  additionalInfo,
  status,
  updatedAt,
  captador,
}: PropertyCardProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const userCollaborator = localStorage.getItem("userCollaborator");
  const isAdmin = userCollaborator === "ADMIN";

  // Gerar o ID do imóvel
  const propertyId = generatePropertyId(type, codigo, id);

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  };

  const handleDelete = async () => {
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        alert("Usuário não autenticado.");
        return;
      }

      const response = await fetch(`${route}/api/properties/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Falha ao excluir a propriedade.");
      }

      alert("Imóvel excluído com sucesso!");
      window.location.reload();
    } catch (error) {
      console.error("Erro ao excluir o Imóvel:", error);
      alert("Erro ao excluir o Imóvel.");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "À venda":
        return "bg-green-500";
      case "Locação":
        return "bg-blue-500";
      case "Vendido":
        return "bg-red-500";
      case "Suspenso":
        return "bg-yellow-500";
      case "Disponível":
        return "bg-emerald-500";
      case "Lançamento":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <>
      <div className="mt-6 flex w-full justify-center">
        <div className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-xl border bg-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl sm:flex-row">
          <div className="relative h-80 w-full sm:h-auto sm:w-1/2">
            {images.length > 0 ? (
              <>
                <img
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  src={images[currentImageIndex]}
                  alt={title}
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-3 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30"
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="text-lg"
                      />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-3 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30"
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="text-lg"
                      />
                    </button>

                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 space-x-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`h-2 w-2 rounded-full transition-all duration-300 ${
                            index === currentImageIndex
                              ? "w-6 bg-white"
                              : "bg-white/50 hover:bg-white/75"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <div className="text-center">
                  <i className="fas fa-image mb-2 text-4xl text-gray-400"></i>
                  <p className="text-sm text-gray-500">Sem imagem disponível</p>
                </div>
              </div>
            )}

            {/* ID do imóvel no canto superior esquerdo */}
            <div className="absolute left-4 top-4">
              <div className="rounded-lg bg-black/70 px-3 py-1 backdrop-blur-sm">
                <span className="text-xs font-bold text-white">{propertyId}</span>
              </div>
            </div>

            <div
              className={`absolute right-4 top-4 rounded-full px-3 py-1 ${getStatusColor(status)}`}
            >
              <span className="text-xs font-semibold text-white">{status}</span>
            </div>

            <div className="absolute bottom-8 left-4 rounded-lg bg-white/95 p-3 shadow-lg backdrop-blur-sm">
              <p className="text-lg font-bold text-gray-900">{price}</p>
              <div className="flex flex-col space-y-1 text-xs text-gray-600">
                {condominiumPrice !== "N/A" && (
                  <span>Condomínio: {condominiumPrice}</span>
                )}
                {iptuPrice !== "N/A" && <span>IPTU: {iptuPrice}</span>}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col justify-between p-6 sm:w-1/2">
            <div className="mb-4">
              <h3 className="mb-2 text-lg font-bold text-gray-900 sm:text-xl">
                {title}
              </h3>
              <div className="flex items-start space-x-2 text-gray-600">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mt-1 flex-shrink-0 text-sm"
                />
                <p className="text-sm sm:text-base">
                  {(() => {
                    // Dividir a descrição para destacar o nome do condomínio
                    const parts = description.split(" - ");
                    return parts.map((part, index) => (
                      <span key={index}>
                        {index > 0 && " - "}
                        {index === parts.length - 1 || index === parts.length - 2 ? (
                          <b className="font-bold text-gray-900">{part}</b>
                        ) : (
                          part
                        )}
                      </span>
                    ));
                  })()} -{" "}
                  <b className="font-semibold text-gray-800">{status}</b>
                </p>
              </div>
              {additionalInfo && (
                <p className="mt-2 text-xs text-gray-500 sm:text-sm">
                  {additionalInfo}
                </p>
              )}
            </div>

            {attributes.length > 0 && (
              <div className="mb-6">
                <div className="grid grid-cols-2 gap-4">
                  {attributes.slice(0, 4).map((attr, index) => (
                    <div key={index} className="text-center">
                      <p className="text-lg font-bold text-gray-900 sm:text-xl">
                        {attr.value}
                      </p>
                      <p className="text-xs text-gray-600 sm:text-sm">
                        {attr.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-gray-100 pt-4">
              <Link
                to={`/propriedade/${id}`}
                className="flex items-center space-x-2 rounded-lg bg-green-700 px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-green-800 hover:shadow-lg"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Acessar Imóvel</span>
              </Link>

              {isAdmin && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="rounded-lg p-3 text-gray-600 transition-all duration-300 hover:bg-red-50 hover:text-red-700"
                  title="Excluir Imóvel"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-lg" />
                </button>
              )}
            </div>

            {/* Informação de Última Atualização com tagKanlo */}
            {(updatedAt || captador) && (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {updatedAt && (
                      <p className="text-xs text-gray-700">
                        Atualizado {getDaysAgo(updatedAt)}
                        {captador && ` - Captador: ${captador}`}
                      </p>
                    )}
                  </div>
                  <div className="ml-4">
                    {tagKanlo !== "N/A" && (
                      <div className="rounded-lg bg-blue-100 px-3 py-1">
                        <span className="text-xs font-bold text-blue-800">
                          {tagKanlo}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-2xl text-red-600"
                />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                Confirmar Exclusão
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Tem certeza que deseja excluir este imóvel? Esta ação não pode
                ser desfeita.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-3 text-sm font-medium text-white hover:bg-red-700"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
